import { useId } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * SilasSeal - The Wax Seal Primary Mark
 *
 * Two families optimized for different sizes:
 * - Small (<28px): Simplified, max legibility
 * - Large (>=28px): Brass rim, oxblood wax, brass S, depth effects
 *
 * States:
 * - full: High confidence (brass rim + oxblood wax + brass S)
 * - outline: Mid confidence (brass outline)
 * - faded: Overruled (30% opacity full)
 *
 * DOCTRINE: The seal is a credential, not a mystery box.
 * MUST have tooltip explaining confidence and reason.
 */

const LARGE_THRESHOLD = 28;

export type SealState = 'full' | 'outline' | 'faded';

export interface SilasSealProps {
  /** Size in pixels (default: 24) */
  size?: number;
  /** Confidence state */
  state?: SealState;
  /** Additional CSS classes */
  className?: string;
  /** Tooltip content (REQUIRED - seal must always explain itself) */
  tooltip: string;
}

export function SilasSeal({ size = 24, state = 'full', className = '', tooltip }: SilasSealProps) {
  const isLarge = size >= LARGE_THRESHOLD;

  const seal = isLarge ? (
    <SealLarge size={size} state={state} className="" />
  ) : (
    <SealSmall size={size} state={state} className="" />
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex cursor-pointer ${className}`} aria-label="Silas seal">
            {seal}
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface SealVariantProps {
  size: number;
  state: SealState;
  className: string;
}

function SealSmall({ size, state, className }: SealVariantProps) {
  if (state === 'full') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-label="Silas seal"
        role="img"
      >
        <circle cx="12" cy="12" r="10.5" fill="#C9A87C" stroke="#A8875A" strokeWidth="0.5" />
        <circle cx="12" cy="12" r="8" fill="#8B3A3A" />
        <path
          d="M 14.5 7 C 13 6.5, 10.5 6.8, 9.5 8 C 8.5 9.2, 9 10.5, 10 11.2 C 11 11.9, 13 12.5, 14 13.3 C 15.2 14.3, 15.5 16, 14 17.2 C 12.5 18.4, 10 18, 8.5 17"
          fill="none"
          stroke="#C9A87C"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M 14.5 7 L 16 6.8" stroke="#C9A87C" strokeWidth="2" strokeLinecap="square" />
        <path d="M 8.5 17 L 7 17.2" stroke="#C9A87C" strokeWidth="2" strokeLinecap="square" />
      </svg>
    );
  }

  if (state === 'outline') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-label="Silas seal"
        role="img"
      >
        <circle
          cx="12"
          cy="12"
          r="10.5"
          fill="none"
          stroke="#C9A87C"
          strokeWidth="1.5"
          opacity="0.7"
        />
        <circle cx="12" cy="12" r="8" fill="none" stroke="#8B3A3A" strokeWidth="1" opacity="0.5" />
        <g opacity="0.7">
          <path
            d="M 14.5 7 C 13 6.5, 10.5 6.8, 9.5 8 C 8.5 9.2, 9 10.5, 10 11.2 C 11 11.9, 13 12.5, 14 13.3 C 15.2 14.3, 15.5 16, 14 17.2 C 12.5 18.4, 10 18, 8.5 17"
            fill="none"
            stroke="#C9A87C"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M 14.5 7 L 16 6.8" stroke="#C9A87C" strokeWidth="2" strokeLinecap="square" />
          <path d="M 8.5 17 L 7 17.2" stroke="#C9A87C" strokeWidth="2" strokeLinecap="square" />
        </g>
      </svg>
    );
  }

  // faded state
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      opacity="0.3"
      aria-label="Silas seal"
      role="img"
    >
      <circle cx="12" cy="12" r="10.5" fill="#C9A87C" stroke="#A8875A" strokeWidth="0.5" />
      <circle cx="12" cy="12" r="8" fill="#8B3A3A" />
      <path
        d="M 14.5 7 C 13 6.5, 10.5 6.8, 9.5 8 C 8.5 9.2, 9 10.5, 10 11.2 C 11 11.9, 13 12.5, 14 13.3 C 15.2 14.3, 15.5 16, 14 17.2 C 12.5 18.4, 10 18, 8.5 17"
        fill="none"
        stroke="#C9A87C"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M 14.5 7 L 16 6.8" stroke="#C9A87C" strokeWidth="2" strokeLinecap="square" />
      <path d="M 8.5 17 L 7 17.2" stroke="#C9A87C" strokeWidth="2" strokeLinecap="square" />
    </svg>
  );
}

function SealLarge({ size, state, className }: SealVariantProps) {
  const id = useId();

  if (state === 'full') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-label="Silas seal"
        role="img"
      >
        <defs>
          <linearGradient id={`brassRim-${id}`} x1="10%" y1="10%" x2="90%" y2="90%">
            <stop offset="0%" stopColor="#E8DAB8" />
            <stop offset="25%" stopColor="#D4BC8E" />
            <stop offset="50%" stopColor="#C9A87C" />
            <stop offset="75%" stopColor="#B8956A" />
            <stop offset="100%" stopColor="#9A7A52" />
          </linearGradient>
          <radialGradient id={`waxGrad-${id}`} cx="35%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#9B4848" />
            <stop offset="60%" stopColor="#8B3A3A" />
            <stop offset="100%" stopColor="#5B2525" />
          </radialGradient>
          <linearGradient id={`brassS-${id}`} x1="20%" y1="20%" x2="80%" y2="80%">
            <stop offset="0%" stopColor="#E5D4B0" />
            <stop offset="40%" stopColor="#D4BC8E" />
            <stop offset="60%" stopColor="#C9A87C" />
            <stop offset="100%" stopColor="#B8956A" />
          </linearGradient>
        </defs>
        <circle
          cx="24"
          cy="24"
          r="22"
          fill={`url(#brassRim-${id})`}
          stroke="#8A6A42"
          strokeWidth="0.5"
        />
        <circle
          cx="24"
          cy="24"
          r="21.5"
          fill="none"
          stroke="#F5E5C5"
          strokeWidth="0.3"
          strokeOpacity="0.4"
        />
        <circle cx="24" cy="24" r="17" fill={`url(#waxGrad-${id})`} />
        <g>
          <path
            d="M 28 13 C 25.5 12, 21.5 12.5, 19.5 14.5 C 17.5 16.5, 18 19, 20 20.5 C 21.5 21.5, 24 22.5, 26 23.5 C 28.5 25, 29.5 27.5, 28 30 C 26 33, 22 33.5, 19 32"
            fill="none"
            stroke={`url(#brassS-${id})`}
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M 28 13 L 30 12.5"
            stroke={`url(#brassS-${id})`}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <path
            d="M 19 32 L 17 32.5"
            stroke={`url(#brassS-${id})`}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </g>
      </svg>
    );
  }

  if (state === 'outline') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-label="Silas seal"
        role="img"
      >
        <circle cx="24" cy="24" r="22" fill="none" stroke="#C9A87C" strokeWidth="2" opacity="0.6" />
        <circle
          cx="24"
          cy="24"
          r="17"
          fill="none"
          stroke="#8B3A3A"
          strokeWidth="1.5"
          opacity="0.5"
        />
        <g opacity="0.6">
          <path
            d="M 28 13 C 25.5 12, 21.5 12.5, 19.5 14.5 C 17.5 16.5, 18 19, 20 20.5 C 21.5 21.5, 24 22.5, 26 23.5 C 28.5 25, 29.5 27.5, 28 30 C 26 33, 22 33.5, 19 32"
            fill="none"
            stroke="#C9A87C"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M 28 13 L 30 12.5" stroke="#C9A87C" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 19 32 L 17 32.5" stroke="#C9A87C" strokeWidth="2.5" strokeLinecap="round" />
        </g>
      </svg>
    );
  }

  // faded state - reuse same id since only one state renders at a time
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      opacity="0.3"
      aria-label="Silas seal"
      role="img"
    >
      <defs>
        <linearGradient id={`brassRimFaded-${id}`} x1="10%" y1="10%" x2="90%" y2="90%">
          <stop offset="0%" stopColor="#E8DAB8" />
          <stop offset="50%" stopColor="#C9A87C" />
          <stop offset="100%" stopColor="#9A7A52" />
        </linearGradient>
        <radialGradient id={`waxGradFaded-${id}`} cx="35%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#9B4848" />
          <stop offset="60%" stopColor="#8B3A3A" />
          <stop offset="100%" stopColor="#5B2525" />
        </radialGradient>
        <linearGradient id={`brassSFaded-${id}`} x1="20%" y1="20%" x2="80%" y2="80%">
          <stop offset="0%" stopColor="#E5D4B0" />
          <stop offset="50%" stopColor="#C9A87C" />
          <stop offset="100%" stopColor="#B8956A" />
        </linearGradient>
      </defs>
      <circle
        cx="24"
        cy="24"
        r="22"
        fill={`url(#brassRimFaded-${id})`}
        stroke="#8A6A42"
        strokeWidth="0.5"
      />
      <circle cx="24" cy="24" r="17" fill={`url(#waxGradFaded-${id})`} />
      <path
        d="M 28 13 C 25.5 12, 21.5 12.5, 19.5 14.5 C 17.5 16.5, 18 19, 20 20.5 C 21.5 21.5, 24 22.5, 26 23.5 C 28.5 25, 29.5 27.5, 28 30 C 26 33, 22 33.5, 19 32"
        fill="none"
        stroke={`url(#brassSFaded-${id})`}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M 28 13 L 30 12.5"
        stroke={`url(#brassSFaded-${id})`}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M 19 32 L 17 32.5"
        stroke={`url(#brassSFaded-${id})`}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Convenience exports for specific states
export function SilasSealFull(props: Omit<SilasSealProps, 'state'>) {
  return <SilasSeal {...props} state="full" />;
}

export function SilasSealOutline(props: Omit<SilasSealProps, 'state'>) {
  return <SilasSeal {...props} state="outline" />;
}

export function SilasSealFaded(props: Omit<SilasSealProps, 'state'>) {
  return <SilasSeal {...props} state="faded" />;
}

export default SilasSeal;
